import type { AnalyticsFailureReason } from "@/lib/analyticsFailureReason";
import { sendGoogleAdsConversion, syncGoogleAnalyticsUser } from "@/lib/site-tracking/google-tag";
import { trackClientEvent } from "@/lib/site-tracking/trackClientEvent";
import { GOOGLE_ADS_ACCOUNT_CREATION_SEND_TO } from "@/lib/tracking-constants";

export const ACCOUNT_CREATION_CALLBACK_PARAM = "account_created";
export const TELEGRAM_CONNECTION_CALLBACK_PARAM = "telegram_connected";

export type BrowserPushAnalyticsResult =
  | "success"
  | "already_granted"
  | "granted"
  | "dismissed"
  | "permission_denied"
  | "subscribe_error"
  | "session_error"
  | "bind_error"
  | "state_refresh_error";

export interface AnalyticsPageContext {
  page_type: string;
  content_family: string;
  entity_type: string;
  seo_quality_tier: string;
}

export interface AnalyticsViewerIdentity {
  userId: string | null;
  userType: "anonymous" | "guest" | "member" | "admin";
  locale: "en" | "pt" | "es";
}

function boundedSurface(value: string): string {
  const normalized = value
    .toLowerCase()
    .replaceAll(/[^a-z0-9_:-]/gu, "_")
    .slice(0, 64);
  return normalized || "unknown";
}

function clientEntityType(value: string | undefined): "release" | "work" {
  return value === "work" ? "work" : "release";
}

export function shouldLoadAnalytics(pathname: string): boolean {
  const cleanPathname = pathname.split(/[?#]/, 1)[0] ?? pathname;
  return cleanPathname !== "/admin" && !cleanPathname.startsWith("/admin/");
}

export function buildAnalyticsPageContext(pathname: string): AnalyticsPageContext {
  const cleanPathname = pathname.split(/[?#]/, 1)[0] ?? pathname;
  const segments = cleanPathname.split("/").filter(Boolean);
  const first = segments[0] ?? "";
  const catalogEntity = catalogEntityType(first);
  if (catalogEntity) {
    return {
      page_type: "catalog_entity",
      content_family: "catalog",
      entity_type: catalogEntity,
      seo_quality_tier: "unknown",
    };
  }

  const editorial = localizedEditorialContext(segments);
  if (editorial) return editorial;
  const known: Record<string, Omit<AnalyticsPageContext, "seo_quality_tier">> = {
    "": { page_type: "home", content_family: "home", entity_type: "none" },
    search: { page_type: "search_results", content_family: "search", entity_type: "mixed" },
    offers: { page_type: "offer_collection", content_family: "commerce", entity_type: "offer" },
    alerts: { page_type: "alerts_hub", content_family: "messaging", entity_type: "none" },
    whatsapp: { page_type: "community_landing", content_family: "messaging", entity_type: "none" },
    listings: {
      page_type: "marketplace_listing",
      content_family: "commerce",
      entity_type: "listing",
    },
    u: { page_type: "public_profile", content_family: "community", entity_type: "profile" },
    admin: { page_type: "admin", content_family: "internal", entity_type: "none" },
    onboarding: { page_type: "onboarding", content_family: "account", entity_type: "none" },
  };
  const localizedFamily = localizedRouteFamily(segments);
  const match = known[first] ?? {
    page_type: localizedFamily === "pages" ? "landing_page" : "other",
    content_family: localizedFamily === "pages" ? "editorial" : "product",
    entity_type: "none",
  };
  return { ...match, seo_quality_tier: "not_applicable" };
}

function catalogEntityType(value: string): "artist" | "company" | "release" | "work" | null {
  return ["artist", "company", "release", "work"].includes(value)
    ? (value as "artist" | "company" | "release" | "work")
    : null;
}

function localizedRouteFamily(segments: string[]): string | null {
  return ["en", "pt", "es"].includes(segments[0] ?? "") ? (segments[1] ?? null) : null;
}

function localizedEditorialContext(segments: string[]): AnalyticsPageContext | null {
  const family = localizedRouteFamily(segments);
  if (!family || !["genres", "styles", "collections", "stories"].includes(family)) return null;
  return {
    page_type: segments.length >= 3 ? "editorial_feature" : "editorial_index",
    content_family: family,
    entity_type: "none",
    seo_quality_tier: "not_applicable",
  };
}

export function trackSignupStarted(
  method: "google" | "telegram" | "phone" | "email",
  surface: string,
): void {
  trackClientEvent({
    name: "authentication_started",
    properties: { method, intent: "sign_up", surface: boundedSurface(surface) },
  });
}

export function trackLoginStarted(
  method: "google" | "telegram" | "phone" | "email",
  surface: string,
): void {
  trackClientEvent({
    name: "authentication_started",
    properties: { method, intent: "login", surface: boundedSurface(surface) },
  });
}

export function trackOnboardingStepViewed(step: number): void {
  trackClientEvent({ name: "onboarding_step_viewed", properties: { step } });
}

export function trackCatalogSearchSubmitted(surface: string): void {
  trackClientEvent({
    name: "catalog_search_submitted",
    properties: { surface: boundedSurface(surface) },
  });
}

export function trackOfferClicked(): void {
  trackClientEvent({ name: "offer_clicked", properties: {} });
}

function resultCountBucket(count: number): "0" | "1_10" | "11_50" | "51_100" | "101_plus" {
  if (count === 0) return "0";
  if (count <= 10) return "1_10";
  if (count <= 50) return "11_50";
  if (count <= 100) return "51_100";
  return "101_plus";
}

export function trackCatalogResultsViewed(surface: string, resultCount: number): void {
  const count = Math.max(0, Math.trunc(resultCount));
  trackClientEvent({
    name: "catalog_results_viewed",
    properties: {
      surface: boundedSurface(surface),
      result_state: count === 0 ? "empty" : "available",
      result_count_bucket: resultCountBucket(count),
    },
  });
}

export function trackOnboardingCompleted(completionPath: string): void {
  trackClientEvent({
    name: "onboarding_completed",
    properties: { completion_path: boundedSurface(completionPath) },
  });
}

export function trackPostOnboardingViewed(recordCount: number): void {
  const count = Math.min(6, Math.max(0, Math.trunc(recordCount)));
  const recordCountBucket = count === 0 ? "0" : count <= 3 ? "1_3" : "4_6";
  trackClientEvent({
    name: "post_onboarding_viewed",
    properties: {
      history_state: count === 0 ? "empty" : "available",
      record_count_bucket: recordCountBucket,
    },
  });
}

export function trackTastePreferencesUpdated(formatCount: number, styleCount: number): void {
  trackClientEvent({
    name: "taste_preferences_updated",
    properties: {
      format_count: Math.min(20, Math.max(0, Math.trunc(formatCount))),
      style_count: Math.min(100, Math.max(0, Math.trunc(styleCount))),
    },
  });
}

export function trackWishlistAddStarted(entityType: string, surface: string): void {
  trackClientEvent({
    name: "wishlist_add_started",
    properties: { entity_type: clientEntityType(entityType), surface: boundedSurface(surface) },
  });
}

export function trackWishlistAddFailed(
  entityType: string,
  surface: string,
  reason: AnalyticsFailureReason,
): void {
  trackClientEvent({
    name: "wishlist_add_failed",
    properties: {
      entity_type: clientEntityType(entityType),
      failure_reason: reason,
      surface: boundedSurface(surface),
    },
  });
}

export function trackWishlistItemAdded(entityType: string, surface: string, itemCount = 1): void {
  trackClientEvent({
    name: "wishlist_item_added",
    properties: {
      entity_type: clientEntityType(entityType),
      item_count: itemCount,
      surface: boundedSurface(surface),
    },
  });
}

export function trackCollectionItemAdded(entityType: string, surface: string, itemCount = 1): void {
  trackClientEvent({
    name: "collection_item_added",
    properties: {
      entity_type: clientEntityType(entityType),
      item_count: itemCount,
      surface: boundedSurface(surface),
    },
  });
}

export function trackNotificationChannelSetupStarted(
  channel: "email" | "telegram" | "whatsapp" | "web_push",
  surface: string,
): void {
  trackClientEvent({
    name: "notification_setup_started",
    properties: { channel, surface: boundedSurface(surface) },
  });
}

export function trackNotificationChannelPromptViewed(surface: string): void {
  trackClientEvent({
    name: "notification_prompt_viewed",
    properties: { surface: boundedSurface(surface) },
  });
}

export function trackNotificationChannelPromptDismissed(surface: string): void {
  trackClientEvent({
    name: "notification_prompt_dismissed",
    properties: { surface: boundedSurface(surface) },
  });
}

export function trackNotificationChannelSetupFailed(
  channel: "email" | "telegram" | "whatsapp" | "web_push",
  surface: string,
  reason: string,
): void {
  trackClientEvent({
    name: "notification_setup_failed",
    properties: {
      channel,
      failure_reason: boundedSurface(reason),
      surface: boundedSurface(surface),
    },
  });
}

export function trackNotificationChannelEnabled(
  channel: "email" | "telegram" | "whatsapp" | "web_push",
  surface: string,
): void {
  trackClientEvent({
    name: "notification_channel_enabled",
    properties: { channel, surface: boundedSurface(surface) },
  });
}

export function trackAccountCreation(
  method: "google" | "telegram" | "phone" | "email",
  userId: string,
): void {
  // Keep the direct Ads conversion independent from GA4 product events.
  sendGoogleAdsConversion(GOOGLE_ADS_ACCOUNT_CREATION_SEND_TO);
  syncGoogleAnalyticsUser(userId);
  trackClientEvent({ name: "sign_up", properties: { method } });
}

export function trackLogin(method: "google" | "telegram" | "phone" | "email"): void {
  trackClientEvent({ name: "login", properties: { method } });
}

export function trackWhatsAppChannelJoinClicked(details: {
  destination: "vinyl" | "cd" | "community";
  surface: string;
}): void {
  trackClientEvent({
    name: "public_channel_join_clicked",
    properties: {
      channel: "whatsapp",
      destination: details.destination,
      surface: boundedSurface(details.surface),
    },
  });
}

export function consumeAccountCreationCallback(userId: string | null): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  const method = url.searchParams.get(ACCOUNT_CREATION_CALLBACK_PARAM);
  if ((method !== "google" && method !== "telegram") || !userId) return;
  url.searchParams.delete(ACCOUNT_CREATION_CALLBACK_PARAM);
  window.history.replaceState(window.history.state, "", url.toString());
  trackAccountCreation(method, userId);
}

export function consumeTelegramConnectionCallback(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (url.searchParams.get(TELEGRAM_CONNECTION_CALLBACK_PARAM) !== "true") return;
  url.searchParams.delete(TELEGRAM_CONNECTION_CALLBACK_PARAM);
  window.history.replaceState(window.history.state, "", url.toString());
}

export function trackCatalogPageView(input: {
  entityType: "artist" | "company" | "release" | "work";
  qualityTier: number;
}): void {
  if (input.entityType !== "release" && input.entityType !== "work") return;
  trackClientEvent({
    name: "catalog_item_viewed",
    properties: { entity_type: input.entityType, surface: "catalog_detail" },
  });
}

export function syncAnalyticsViewer(viewer: AnalyticsViewerIdentity): void {
  if (typeof window !== "undefined" && !shouldLoadAnalytics(window.location.pathname)) return;
  syncGoogleAnalyticsUser(viewer.userId);
}
